<template>
  <div class="v2026-csch">

    <div class="v2026-csch-head">
      <span class="v2026-csch-label">ตารางคุณลูกค้า</span>
      <!-- Directive on the wrapper: Font Awesome's JS replaces the <i> with an <svg>, dropping any
           directive bound to the <i> itself. -->
      <span class="v2026-csch-hint" role="img" tabindex="0"
            aria-label="เงื่อนไขการแสดงผลตารางคุณลูกค้า"
            v-b-tooltip.hover.focus.left.html="{ title: conditionsHint, customClass: 'v2026-csch-tip' }">
        <i class="fas fa-info-circle" aria-hidden="true"></i>
      </span>
    </div>

    <div v-if="!userId" class="v2026-csch-empty">ไม่พบบัญชีลูกค้าในระบบ</div>

    <template v-else>
      <!-- Only when the same person has other accounts: bookings made under one of them never show
           on the linked account. Clicking a card only VIEWS that account's calendar; re-linking the
           chat is the explicit "สลับใช้บัญชีนี้" button, which the panel confirms in a modal. -->
      <div v-if="accounts.length > 1" class="v2026-csch-accounts">
        <div class="v2026-csch-accounts__head">
          <span class="v2026-csch-label">Account ที่ใช้เบอร์โทร/อีเมลเดียวกัน</span>
          <span class="v2026-csch-hint" role="img" tabindex="0"
                aria-label="วิธีใช้รายการบัญชี: ดูการจอง และสลับบัญชี"
                v-b-tooltip.hover.focus.left.html="{ title: accountsHint, customClass: 'v2026-csch-tip' }">
            <i class="fas fa-info-circle" aria-hidden="true"></i>
          </span>
          <button type="button" class="v2026-csch-accounts__toggle"
                  :aria-expanded="accountsOpen ? 'true' : 'false'"
                  :aria-label="accountsOpen ? 'ยุบรายการบัญชี' : 'แสดงรายการบัญชี'"
                  @click="accountsOpen = !accountsOpen">
            <i class="fas fa-chevron-down v2026-csch-accounts__chev" :class="{ 'is-open': accountsOpen }"
               aria-hidden="true"></i>
          </button>
        </div>
        <div v-show="accountsOpen" class="v2026-csch-accounts__list">
          <div v-for="acc in accounts" :key="acc.id"
               class="v2026-csch-acc" :class="{ 'is-active': acc.id === activeUserId }"
               role="button" tabindex="0"
               :aria-pressed="acc.id === activeUserId ? 'true' : 'false'"
               :aria-label="`ดูตารางการจองของบัญชี ID ${acc.id}`"
               @click="selectAccount(acc.id)"
               @keydown.enter.self.prevent="selectAccount(acc.id)"
               @keydown.space.self.prevent="selectAccount(acc.id)">
            <img class="v2026-csch-acc__avatar" :src="acc.avatarUrl" alt="" @error="onAvatarError">
            <div class="v2026-csch-acc__text">
              <div class="v2026-csch-acc__name">{{ acc.name || '-' }}</div>
              <div class="v2026-csch-acc__meta">
                <span v-if="acc.isCurrent" class="v2026-csch-acc__current">ปัจจุบัน</span>
                User ID: <a class="v2026-csch-link" :href="acc.href" target="_blank" @click.stop>{{ acc.id }}</a>
              </div>
            </div>
            <button v-if="!acc.isCurrent && canSwitch" type="button" class="v2026-csch-acc__switch"
                    :aria-label="`สลับให้แชทนี้ใช้บัญชี ID ${acc.id}`"
                    @click.stop="$emit('switch', acc.id)">สลับใช้บัญชีนี้</button>
          </div>
        </div>
      </div>

      <MonthCalendarV2026 :month="month"
                          :bookingsByDay="ordersByDay"
                          :selectedDate="selectedDay"
                          :showCreateButton="false"
                          @month-change="onMonthChange"
                          @select="dateStr => { selectedDay = dateStr }" />

      <LoadingV2026 v-if="loading" label="กำลังโหลดตารางการจอง" />

      <div v-else-if="error" class="v2026-csch-empty">โหลดตารางการจองไม่สำเร็จ</div>

      <template v-else-if="selectedDay">
        <div class="v2026-csch-head">
          <span class="v2026-csch-label">วันที่เลือก ({{ selectedDayOrders.length }})</span>
        </div>
        <div v-if="!selectedDayOrders.length" class="v2026-csch-empty">ไม่มีการจองในวันนี้</div>
        <BookingCardV2026 v-for="o in selectedDayOrders" :key="o.id" :order="o">
          <div class="v2026-csch-chips">
            <span class="v2026-csch-chip" :class="statusOf(o).cls"
                  v-b-tooltip.hover.top :title="statusOf(o).hint">{{ statusOf(o).label }}</span>
            <span v-if="Number(o.paid_status) !== 1" class="v2026-csch-chip is-unpaid"
                  v-b-tooltip.hover.top title="ลูกค้ายังไม่ได้ชำระเงินสำหรับการจองนี้">ยังไม่ชำระ</span>
          </div>
          <div class="v2026-csch-meta">
            <span class="v2026-csch-meta__key">แม่บ้าน:</span>
            <a v-if="o.professional" class="v2026-csch-link" target="_blank"
               :href="maidLink(o.professional.id)">ID:{{ o.professional.id }} {{ maidName(o.professional) }}</a>
            <span v-else class="v2026-csch-meta__none">ยังไม่มีแม่บ้านรับงาน</span>
          </div>
          <div v-if="o.total_price_with_vat != null" class="v2026-csch-meta">
            <span class="v2026-csch-meta__key">ราคา:</span>
            <span>฿{{ formatPrice(o.total_price_with_vat) }}</span>
          </div>
        </BookingCardV2026>
      </template>

      <div v-else class="v2026-csch-empty">
        {{ orders.length ? 'เลือกวันที่เพื่อดูการจอง' : 'ไม่มีการจองในเดือนนี้' }}
      </div>
    </template>

  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import LoadingV2026 from './LoadingV2026.vue'
import MonthCalendarV2026 from '../calendar/MonthCalendarV2026.vue'
import BookingCardV2026 from './BookingCardV2026.vue'

// tb_order.status — see chat-server helpers/chatbot/booking.service.js. Cancelled orders never
// reach here (the endpoint filters cancel = 0), so status 2 always means the job was done.
const STATUS = {
  0: { label: 'ใหม่', cls: 'is-new', hint: 'ยังไม่มีแม่บ้านรับงาน' },
  1: { label: 'รับงานแล้ว', cls: 'is-accepted', hint: 'แม่บ้านรับงานแล้ว รอถึงวันทำงาน' },
  2: { label: 'เสร็จสิ้น', cls: 'is-done', hint: 'แม่บ้านทำงานเสร็จแล้ว' }
}

// Must match the filters in chat-server userController.getUserOrders — change both together.
// Static markup only (rendered as tooltip HTML), never interpolate order or customer data here.
const CONDITIONS_HINT = [
  '<strong>แสดง</strong>',
  '• การจองของลูกค้าคนนี้ในเดือนที่เลือก (ตามวันที่ทำงาน)',
  '• ทุกสถานะ: ใหม่ / รับงานแล้ว / เสร็จสิ้น',
  '• ทั้งที่ชำระแล้ว และยังไม่ชำระ',
  '<strong>ไม่แสดง</strong>',
  '• การจองที่ยกเลิกแล้ว',
  '• การโอนเงินที่หมดเวลาชำระ',
  '• การจองที่ยังไม่ระบุวันที่ทำงาน',
  'ปฏิทินจะเลือกวันที่มีการจองใกล้ที่สุดตั้งแต่วันนี้ให้อัตโนมัติ',
  'ถ้าลูกค้ามีหลายบัญชี จะแสดงเฉพาะการจองของบัญชีที่เลือกอยู่ในรายการบัญชีด้านบน'
].join('<br>')

// How the account list works — the ⓘ beside "Account ที่ใช้เบอร์โทร/อีเมลเดียวกัน".
// Static markup only (rendered as tooltip HTML), never interpolate customer data here.
const ACCOUNTS_HINT = [
  '<strong>กดที่การ์ดบัญชี</strong>',
  '• ดูการจองของบัญชีนั้นในปฏิทินด้านล่าง (กรอบสีฟ้า = กำลังดูอยู่)',
  '• ดูอย่างเดียว ไม่เปลี่ยนการผูกแชท',
  '<strong>ปัจจุบัน</strong>',
  '• บัญชีที่แชทนี้ผูกอยู่ — ข้อมูลลูกค้า ตารางการจอง และ AI ในแชทนี้ใช้บัญชีนี้',
  '<strong>ปุ่ม "สลับใช้บัญชีนี้"</strong>',
  '• ใช้เมื่อลูกค้าจองด้วยบัญชีอื่น แล้วแชทนี้มองไม่เห็นการจอง',
  '• ให้แชทนี้ผูกกับบัญชีนั้นแทน (มีหน้าต่างให้ยืนยันก่อน) — ข้อมูลลูกค้า ตารางการจอง บอท AI และ AI assistant จะเปลี่ยนไปใช้บัญชีนั้น',
  '• ระบบจะไม่เปลี่ยนกลับเอง — กด "สลับใช้บัญชีนี้" ที่บัญชีเดิมเพื่อย้อนกลับ',
  '• ไม่กระทบบัญชีลูกค้าในระบบ BeNeat: ลูกค้ายังล็อกอิน LINE เข้าบัญชีเดิม และการจองที่ลูกค้าทำเองผ่าน LINE จะเข้าบัญชีที่ LINE ผูกอยู่'
].join('<br>')

const PLACEHOLDER_AVATAR = 'https://beneat.co/static/img/unknown.png'

// A customer's own bookings for one month (GET /beneat/users/:userId/orders). Self-contained on
// purpose: ScheduleTabV2026 is shared with the maid and applicant panels, so it stays untouched.
export default {
  name: 'customer-schedule-v2026',
  components: {
    LoadingV2026,
    MonthCalendarV2026,
    BookingCardV2026
  },
  props: {
    // BeNeat tb_user.id — the same identity as the panel's system notes and stats.
    userId: {
      type: [Number, String],
      default: null
    },
    // The linked account first (isCurrent), then the same person's other accounts — each
    // { id, name, avatarUrl, href, isCurrent }. One entry or none = no account list.
    accounts: {
      type: Array,
      default: () => []
    },
    // Whether the "สลับใช้บัญชีนี้" button is offered (the panel needs a chat to re-link).
    canSwitch: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      selectedAccountId: null, // null = the chat's own account (userId)
      accountsOpen: true,
      month: moment().startOf('month'),
      selectedDay: null, // 'YYYY-MM-DD'
      orders: [],
      loading: false,
      error: false,
      requestSeq: 0, // the panel is patched, not remounted, across chats — drop late responses
      conditionsHint: CONDITIONS_HINT,
      accountsHint: ACCOUNTS_HINT
    }
  },
  computed: {
    activeUserId () {
      return this.selectedAccountId || this.userId
    },
    ordersByDay () {
      const map = {}
      this.orders.forEach(o => {
        if (!o.cleaning_date) return
        const key = moment(o.cleaning_date).format('YYYY-MM-DD')
        if (!map[key]) map[key] = []
        map[key].push(o)
      })
      return map
    },
    selectedDayOrders () {
      return this.selectedDay ? (this.ordersByDay[this.selectedDay] || []) : []
    }
  },
  watch: {
    // Another chat: start over on this month rather than the month viewed for the previous customer.
    userId: {
      immediate: true,
      handler () {
        this.selectedAccountId = null
        this.month = moment().startOf('month')
        this.selectedDay = null
        this.orders = []
        this.error = false
        this.fetchOrders()
      }
    },
    // The list arrives with the search-user response and can change under us; a picked account
    // that is no longer listed falls back to the chat's own.
    accounts (list) {
      if (this.selectedAccountId && !list.some(acc => acc.id === this.selectedAccountId)) {
        this.selectAccount(null)
      }
    }
  },
  methods: {
    async fetchOrders () {
      const seq = ++this.requestSeq
      if (!this.userId) {
        this.loading = false
        return
      }
      this.loading = true
      this.error = false
      try {
        const { data } = await axios.get(`/beneat/users/${this.activeUserId}/orders`, {
          params: { month: this.month.format('YYYY-MM') }
        })
        if (seq !== this.requestSeq) return
        this.orders = (data && Array.isArray(data.data)) ? data.data : []
        this.autoSelectDay()
      } catch (e) {
        if (seq !== this.requestSeq) return
        this.orders = []
        this.error = true
      } finally {
        if (seq === this.requestSeq) this.loading = false
      }
    },
    // Swap to the placeholder if an avatar fails; guarded against a fallback loop.
    onAvatarError (e) {
      if (e && e.target && e.target.src !== PLACEHOLDER_AVATAR) {
        e.target.src = PLACEHOLDER_AVATAR
      }
    },
    // Keeps the month being viewed — the admin is usually comparing the same dates across accounts.
    selectAccount (id) {
      const next = (id === this.userId) ? null : id
      if (next === this.selectedAccountId) return
      this.selectedAccountId = next
      this.selectedDay = null
      this.orders = []
      this.fetchOrders()
    },
    onMonthChange (ym) {
      this.month = moment(ym, 'YYYY-MM').startOf('month')
      this.selectedDay = null
      this.orders = []
      this.fetchOrders()
    },
    // Opens on the nearest booking from today on, so the admin sees what is coming up without a
    // click. Never overrides a day the admin already picked; a past month selects nothing.
    autoSelectDay () {
      if (this.selectedDay) return
      const today = moment().format('YYYY-MM-DD')
      const next = Object.keys(this.ordersByDay).sort().find(day => day >= today)
      if (next) this.selectedDay = next
    },
    statusOf (order) {
      return STATUS[order.status] || { label: '-', cls: '', hint: null }
    },
    maidName (maid) {
      return [maid.firstname, maid.lastname].filter(Boolean).join(' ')
    },
    maidLink (id) {
      return `https://admin-test.beneat.co/professional/${id}/profile`
    },
    formatPrice (value) {
      const n = parseFloat(value)
      return isNaN(n) ? value : n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
  }
}
</script>

<style scoped>
.v2026-csch {
  --v2026-color-main: #31C1D7;
  --v2026-link: #007bff;
  --v2026-text: #101828;
  --v2026-text-secondary: #667085;
  --v2026-text-muted: #98a2b3;
  --v2026-sp-1: 4px;
  --v2026-sp-2: 8px;
  --v2026-sp-3: 12px;
  --v2026-fs-section: 13px;
  --v2026-fs-meta: 12px;

  display: flex;
  flex-direction: column;
  gap: var(--v2026-sp-3);
}

.v2026-csch-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.v2026-csch-label {
  font-size: var(--v2026-fs-section);
  font-weight: 600;
  color: var(--v2026-text-secondary);
}

.v2026-csch-hint {
  font-size: 12px;
  color: var(--v2026-text-secondary);
  cursor: help;
}
.v2026-csch-hint:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
  border-radius: 50%;
}

.v2026-csch-accounts {
  display: flex;
  flex-direction: column;
  gap: var(--v2026-sp-2);
}
.v2026-csch-accounts__head {
  display: flex;
  align-items: center;
  gap: 6px;
}
.v2026-csch-accounts__toggle {
  margin-left: auto;
  padding: 2px 4px;
  border: 0;
  background: none;
  cursor: pointer;
}
.v2026-csch-accounts__toggle:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
  border-radius: 4px;
}
.v2026-csch-accounts__chev {
  font-size: 12px;
  color: var(--v2026-text-secondary);
  transition: transform 150ms ease;
  transform: rotate(-90deg);
}
.v2026-csch-accounts__chev.is-open {
  transform: rotate(0deg);
}
.v2026-csch-accounts__list {
  display: flex;
  flex-direction: column;
  gap: var(--v2026-sp-2);
}
.v2026-csch-acc {
  display: flex;
  align-items: center;
  gap: var(--v2026-sp-3);
  padding: 10px var(--v2026-sp-3);
  border: 1px solid transparent;
  border-radius: 12px;
  background: #F8FAFC;
  cursor: pointer;
  transition: background 150ms ease, border-color 150ms ease;
}
.v2026-csch-acc:hover {
  background: #eef2f6;
}
.v2026-csch-acc.is-active {
  border-color: var(--v2026-color-main);
  background: #31C1D71F;
}
.v2026-csch-acc:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}
.v2026-csch-acc__avatar {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
}
.v2026-csch-acc__text {
  flex: 1;
  min-width: 0;
}
.v2026-csch-acc__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 600;
  color: var(--v2026-text);
}
.v2026-csch-acc__meta {
  font-size: var(--v2026-fs-meta);
  color: var(--v2026-text-secondary);
}
.v2026-csch-acc__current {
  margin-right: var(--v2026-sp-1);
  font-weight: 600;
  color: #079455;
}
.v2026-csch-acc__switch {
  flex: 0 0 auto;
  padding: 4px 10px;
  border: 1px solid var(--v2026-color-main);
  border-radius: 8px;
  background: #ffffff;
  color: #0e6776;
  font-size: var(--v2026-fs-meta);
  font-weight: 600;
  cursor: pointer;
}
.v2026-csch-acc__switch:hover {
  background: #31C1D714;
}
.v2026-csch-acc__switch:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}

.v2026-csch-empty {
  font-size: var(--v2026-fs-meta);
  color: var(--v2026-text-muted);
  padding: var(--v2026-sp-1) 0;
}

.v2026-csch-chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--v2026-sp-1);
}
.v2026-csch-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px var(--v2026-sp-2);
  border-radius: 999px;
  font-size: var(--v2026-fs-meta);
  font-weight: 600;
  line-height: 18px;
  background: #eaecf0;
  color: #344054;
}
.v2026-csch-chip.is-new { background: #fef0c7; color: #93370d; }
.v2026-csch-chip.is-accepted { background: #d1f3f8; color: #0e6776; }
.v2026-csch-chip.is-done { background: #d1fadf; color: #05603a; }
.v2026-csch-chip.is-unpaid { background: #fee4e2; color: #b42318; }

.v2026-csch-meta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--v2026-sp-1);
  font-size: var(--v2026-fs-meta);
  color: var(--v2026-text);
}
.v2026-csch-meta__key {
  color: var(--v2026-text-secondary);
}
.v2026-csch-meta__none {
  color: var(--v2026-text-muted);
}

.v2026-csch-link {
  color: var(--v2026-link);
  text-decoration: none;
}
.v2026-csch-link:hover {
  text-decoration: underline;
}
.v2026-csch-link:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
  border-radius: 3px;
}
</style>

<style>
/* Unscoped: bootstrap-vue mounts the tooltip on <body>, outside this component's scope. */
.v2026-csch-tip .tooltip-inner {
  max-width: 300px;
  text-align: left;
  line-height: 1.6;
}
</style>
